import { Diamond, DisplayConfigurationV2 } from "@src/types/DiamondDetail";
import { JewelryVariation } from "@src/types/JewelryVariationDetails";
import { useGetJewelryVariation } from "@apis/diamondDetail";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Loader from "@components/atoms/Loader";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { DefaultTheme, useTheme } from "styled-components/native";
import {
  Modal as RNModal,
  Platform,
  Pressable,
  StyleSheet,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import {
  useRoute,
  useNavigation,
  useFocusEffect,
} from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import ImageComponentJewelry from "./ImageComponentJewelry";
import { ProductCategory } from "@utils/constants";
import JewelryConfigurationCell, { CellInfo } from "./JewelryConfigurationCell";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Button, Spacer, VDBText, CheckBox } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import currency from "currency.js";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useQueryClient } from "@tanstack/react-query";
import { useBulkActions } from "@apis/collection";
import {
  BulkActionsEnum,
  CartTypeEnum,
} from "@utils/product/searchResultConstants";
import { getStockItemType, isConfigurableJewelryItem } from "./Utils";
import { OrderNowLeaveGuard } from "./orderNowLeaveGuard";
import {
  getProductColor,
  getProductTintColor,
} from "@utils/product/productTheme";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { GetHeaderLeft } from "@navigations/utils";
import { useTranslation } from "react-i18next";
import { sortAllTypes } from "@scenes/configurator/JewelryConfigurator/utils";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import RenderHTML from "react-native-render-html";
import DynamicForm from "@components/molecules/DynamicForm/DynamicForm";
import { filterAndSortFormData } from "@components/molecules/DynamicForm/utils";
import {
  DynamicFormProvider,
  useDynamicFormState,
} from "@scenes/bulk-request/context/DynamicFormContext";
import WebPageHeader from "@components/molecules/WebPageHeader/WebPageHeader";
import { SpaceType } from "@components/atoms/Spacer/types";
import {
  useOrderCart,
  useOrderNowVariationCart,
  useSendQuoteOrderVariationCart,
} from "@redux/slices/globalSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import { getShowEditIcon, RequestType } from "@scenes/bulk-request/Utils";
import apiClient from "@apis/index";
import ApiRoutes from "@apis/routes";
import { RequestTypes } from "@scenes/requests/utils/utils";
import { useShareContent } from "@components/molecules/Share/utils/utils";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import useScreenOptionsHooks from "../../hooks/useScreenOptionsHooks";
import ShippingLocation from "@scenes/configurator/OrderCart/ShippingLocation";
import ShippingMethod from "@scenes/configurator/OrderCart/ShippingMethod";
import ShippingInformation from "@scenes/configurator/OrderCart/ShippingInformation";
import CheckoutForm from "@scenes/configurator/OrderCart/CheckoutForm";
import Tooltip from "@components/atoms/Tooltip";
import TooltipButton from "@scenes/pdp/utils/TooltipButton";
import { useOrderCheckout } from "@apis/cartRequest";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useDispatch, useSelector } from "react-redux";
import {
  updateTotalCartItems,
  useOrderCartSlice,
} from "@redux/slices/orderCartSlice";
import {
  formatPersonalisationText,
  omitQuoteRecipientCommentFields,
} from "@scenes/configurator/utils";
import {
  filterLocationData,
  getAvailableShippingMethods,
  getPrimaryLocation,
} from "@scenes/configurator/OrderCart/utils";
import { FeeResponseItem } from "@scenes/configurator/OrderCart/types";
import Modal from "@components/atoms/Modal";
import SuccessPopUp from "@scenes/configurator/OrderCart/SuccessPopUp";
import EmptyView from "@scenes/configurator/OrderCart/EmptyView";
import ConfirmationPopup from "@components/atoms/ConfirmationPopUp";
import PopUp from "@components/molecules/PopUp";
import { messageType } from "@components/molecules/PopUp/types";
import {
  useGetPdpSectionLayout,
  useGetPdpSectionsOrder,
} from "../../hooks/useGetSectionLayout";
import { PdpComponentNames, PdpSections } from "@utils/customization";
import { getVisiblePdpSections } from "@scenes/pdp/utils/pdpConfigurableComponent";

const NativeShare =
  Platform.OS !== "web" ? require("react-native-share").default : undefined;

type OrderNowJewelryVariationScreenProps = {
  mItem?: Diamond;
  mItems?: Diamond[];
  onClose?: () => void;
  isBuyNow?: boolean;
  send_quote?: boolean;
  productType?: string;
};

const buildNestedParams = (
  prefix: string,
  obj: Record<string, string>
): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const key in obj) {
    result[`${prefix}[${key}]`] = obj[key];
  }
  return result;
};

const buildApiParams = (
  selections: Record<string, string>,
  displayConfig: DisplayConfigurationV2[],
  lastChangedOptionName: string
): Record<string, string> => {
  const lastChangedIndex = displayConfig.findIndex(
    (c) => c.option_name === lastChangedOptionName
  );

  const slugDetail: Record<string, string> = {};
  const partialSlugDetail: Record<string, string> = {};

  displayConfig.forEach((configItem, index) => {
    if (!configItem.option_slug) return;

    const selectedLabel = selections[configItem.option_name];
    if (!selectedLabel) return;

    const matched = configItem.option_value.find(
      (opt) =>
        opt.label === selectedLabel ||
        opt.value === selectedLabel ||
        opt.slug === selectedLabel
    );
    if (matched?.value) {
      slugDetail[configItem.option_type] = matched.value;

      if (lastChangedIndex === -1 || index <= lastChangedIndex) {
        partialSlugDetail[configItem.option_type] = matched.value;
      }
    } else if (matched?.slug || selectedLabel) {
      const fallback = matched?.slug ?? selectedLabel;
      slugDetail[configItem.option_type] = fallback;
      if (lastChangedIndex === -1 || index <= lastChangedIndex) {
        partialSlugDetail[configItem.option_type] = fallback;
      }
    }
  });

  return {
    ...buildNestedParams("slug_details", slugDetail),
    ...buildNestedParams("partial_slug_details", partialSlugDetail),
  };
};

const makeVariantKey = (selections: Record<string, string>) =>
  JSON.stringify(Object.entries(selections).sort());

const canonicalVariantKey = (
  selections: Record<string, string> | undefined,
  displayConfig: DisplayConfigurationV2[] = []
) => {
  const slugOnly: Record<string, string> = {};
  displayConfig.forEach((configItem) => {
    if (!configItem.option_slug) return;
    const raw = selections?.[configItem.option_name];
    if (!raw) return;
    const matched = configItem.option_value.find(
      (opt) => opt.label === raw || opt.value === raw || opt.slug === raw
    );
    slugOnly[configItem.option_name] = matched?.slug ?? matched?.value ?? raw;
  });
  return makeVariantKey(slugOnly);
};

const unwrapJewelryVariation = (raw: any): JewelryVariation | undefined => {
  if (!raw) return undefined;
  if (raw?.response?.body?.variations) return raw as JewelryVariation;
  if (raw?.data?.response?.body?.variations) {
    return raw.data as JewelryVariation;
  }
  if (raw?.data?.data?.response?.body?.variations) {
    return raw.data.data as JewelryVariation;
  }
  return undefined;
};

const getVariationMeta = (raw: any) => {
  const variation = unwrapJewelryVariation(raw);
  const first = variation?.response?.body?.variations?.[0];
  const priceRaw = first?.data?.price ?? first?.item?.total_sales_price;
  const priceNum = priceRaw != null ? Number(priceRaw) : NaN;
  const rawId = first?.data?.id ?? first?.item?.id ?? null;
  const parsedId =
    rawId == null || rawId === ""
      ? null
      : typeof rawId === "number"
      ? rawId
      : Number(rawId);
  return {
    variation,
    variantId: parsedId != null && !Number.isNaN(parsedId) ? parsedId : null,
    price: Number.isFinite(priceNum) ? priceNum : null,
    firstVariation: first,
  };
};

const isOptionShown = (option: DisplayConfigurationV2) => {
  if (!option?.option_visible) return false;
  return (option.option_value ?? []).some((opt) => opt.enabled !== false);
};

const isOptionRequiredForConfirm = (option: DisplayConfigurationV2) =>
  isOptionShown(option);

const hasMatchingOptionSelection = (
  option: DisplayConfigurationV2,
  selections: Record<string, string>
) => {
  const selected = selections[option.option_name];
  if (!selected) return false;
  return (option.option_value ?? []).some(
    (opt) =>
      opt.enabled !== false &&
      (opt.label === selected ||
        opt.value === selected ||
        opt.slug === selected)
  );
};

const buildConfigFromSelections = (
  selections: Record<string, string>,
  displayConfig: DisplayConfigurationV2[]
): Record<string, string> => {
  const config: Record<string, string> = {};
  displayConfig.forEach((configItem) => {
    const selectedLabel = selections[configItem.option_name];
    if (!selectedLabel) return;
    const matched = configItem.option_value.find(
      (opt) =>
        opt.label === selectedLabel ||
        opt.value === selectedLabel ||
        opt.slug === selectedLabel
    );
    if (matched?.value) {
      config[configItem.option_type] = matched.value;
    }
  });
  return config;
};

const compactPersonalization = (values?: Record<string, any>) => {
  if (!values) return {};
  return Object.fromEntries(
    Object.entries(values).filter(
      ([, value]) =>
        value !== null && value !== undefined && String(value).trim() !== ""
    )
  );
};

type OrderCartLine = {
  quantity: number;
  config: Record<string, string>;
  personalization: Record<string, any>;
  item_comments?: string;
};

const cartLineSignature = (line: Omit<OrderCartLine, "quantity">) =>
  JSON.stringify({
    config: line.config,
    personalization: line.personalization,
    item_comments: line.item_comments ?? "",
  });

const hasFilledValue = (value?: unknown) =>
  value != null && String(value).trim() !== "";

const isEngravePairComplete = (personalization?: Record<string, any>) => {
  const hasText = hasFilledValue(personalization?.engrave_text);
  const hasFont = hasFilledValue(personalization?.engrave_font);
  return hasText === hasFont;
};

const cloneJson = <T,>(value: T): T =>
  value == null ? value : JSON.parse(JSON.stringify(value));

const cloneDisplayConfig = (
  displayConfig: DisplayConfigurationV2[] = []
): DisplayConfigurationV2[] => sortAllTypes(cloneJson(displayConfig));

const cloneVariationData = (
  variation?: JewelryVariation
): JewelryVariation | undefined =>
  variation ? cloneJson(variation) : undefined;

type ConfigEntry = {
  id: number;
  baseItem: Diamond;
  variationData?: JewelryVariation;
  displayConfiguration: DisplayConfigurationV2[];
};

const getVariationItem = (
  variationData?: JewelryVariation
): Diamond | undefined => {
  const variationItem =
    unwrapJewelryVariation(variationData)?.response?.body?.variations?.[0]
      ?.item;
  if (!variationItem) return undefined;
  return {
    ...variationItem,
    display_configuration_v2: cloneDisplayConfig(
      variationItem.display_configuration_v2 ?? []
    ),
  };
};

const applyVariationToCell = (
  config: ConfigEntry,
  jewelryData: JewelryVariation
): ConfigEntry => {
  const cloned = cloneVariationData(jewelryData);
  const variationItem = cloned?.response?.body?.variations?.[0]?.item;
  return {
    ...config,
    variationData: cloned,
    displayConfiguration: cloneDisplayConfig(
      variationItem?.display_configuration_v2 ?? config.displayConfiguration
    ),
  };
};

const mergeSelectionsFromVariation = (
  current: Record<string, string>,
  jewelryData?: JewelryVariation
): Record<string, string> => {
  const variationItem =
    unwrapJewelryVariation(jewelryData)?.response?.body?.variations?.[0]?.item;
  if (!variationItem) return current;
  const next = { ...current };
  const slugDetail = variationItem.slug_detail ?? {};
  // Same as PDP: the returned variant's slug_detail replaces selections on
  // this cell only. Options already chosen stay only when this variant does
  // not include that option type.
  variationItem.display_configuration_v2?.forEach((configObject) => {
    if (
      !Object.prototype.hasOwnProperty.call(
        slugDetail,
        configObject.option_type
      )
    ) {
      return;
    }
    const optionValue = slugDetail[configObject.option_type];
    if (typeof optionValue !== "string" || !optionValue) return;
    const normalized = optionValue.toLowerCase().replaceAll(" ", "-");
    const matched = configObject.option_value.find(
      (opt) =>
        opt.slug === optionValue ||
        opt.slug === normalized ||
        opt.value === optionValue
    );
    if (matched?.label) {
      next[configObject.option_name] = matched.label;
    } else {
      delete next[configObject.option_name];
    }
  });
  return next;
};

const cellInfoFromVariation = (
  jewelryData: JewelryVariation | undefined,
  quantity: number,
  fallbackTotal: number | null
): CellInfo | null => {
  const { variantId, price } = getVariationMeta(jewelryData);
  if (variantId == null) return null;
  return {
    quantity,
    variantId,
    totalPrice: price != null ? price * quantity : fallbackTotal,
  };
};

const parseItemId = (id: unknown): number | null => {
  if (id == null || id === "") return null;
  const parsed = typeof id === "number" ? id : Number(id);
  return Number.isNaN(parsed) ? null : parsed;
};

const seedCellInfoFromItem = (item: Diamond): CellInfo => {
  const price = Number(item?.total_sales_price);
  return {
    quantity: 1,
    variantId: parseItemId(item?.id),
    totalPrice: Number.isFinite(price) ? price : null,
  };
};

const resolveCartItemId = (cell: ConfigEntry, info?: CellInfo): number | null =>
  getVariationMeta(cell.variationData).variantId ??
  info?.variantId ??
  parseItemId(cell.baseItem?.id);

/** Build default selections + slug API params from a base Diamond (mItemProp). */
const buildDefaultsFromItem = (item: Diamond) => {
  const displayConfig = item.display_configuration_v2;
  const slugDetail = item.slug_detail;
  const initialSelections: Record<string, string> = {};
  const slugParams: Record<string, string> = {};

  if (!displayConfig?.length || !slugDetail) {
    return { initialSelections, slugParams, slugOnlySelections: {} };
  }

  displayConfig.forEach((configItem) => {
    const rawSlug = slugDetail[configItem.option_type];
    if (typeof rawSlug !== "string" || !rawSlug) return;
    const matched = configItem.option_value.find(
      (opt) => opt.slug === rawSlug || opt.value === rawSlug
    );
    if (matched) {
      initialSelections[configItem.option_name] = matched.label;
      if (configItem.option_slug) {
        slugParams[configItem.option_type] = matched.value;
      }
    }
  });

  const slugOnlySelections: Record<string, string> = {};
  displayConfig.forEach((configItem) => {
    if (configItem.option_slug && initialSelections[configItem.option_name]) {
      slugOnlySelections[configItem.option_name] =
        initialSelections[configItem.option_name];
    }
  });

  return { initialSelections, slugParams, slugOnlySelections };
};

const seedSelectionsFromItem = (item: Diamond): Record<string, string> => {
  const { initialSelections } = buildDefaultsFromItem(item);
  const seeded = { ...initialSelections };
  (item.display_configuration_v2 ?? []).forEach((configItem) => {
    if (seeded[configItem.option_name]) return;
    if (configItem.option_visible) return;
    const firstValue = configItem.option_value?.[0];
    if (firstValue?.label) {
      seeded[configItem.option_name] = firstValue.label;
    }
  });
  return seeded;
};

const normalizeItem = (item: Diamond): Diamond => ({
  ...item,
  display_configuration_v2: sortAllTypes(item?.display_configuration_v2 ?? []),
});

const withPrimaryLocation = (locations: unknown) => {
  if (!Array.isArray(locations) || locations.length === 0) return [];
  if (locations.some((location) => location?.is_primary)) return locations;
  return locations.map((location, index) => ({
    ...location,
    is_primary: index === 0,
  }));
};

const OrderNowRequestForm = ({
  formData,
  orderTypeFormData = [],
  totalNetPrice,
  productType,
  priceMode,
  showFormFirst = false,
  couponCode = "",
  setCouponCode,
  refetchCartData,
  cartData,
  cartAPIFetching = false,
}: {
  formData: any[];
  orderTypeFormData?: any[];
  totalNetPrice: number;
  productType: string;
  priceMode: number;
  showFormFirst?: boolean;
  couponCode?: string;
  setCouponCode?: (code: string) => void;
  refetchCartData?: () => void;
  cartData?: any;
  cartAPIFetching?: boolean;
}) => {
  const { theme, t } = useScreenOptionsHooks();
  const orderCart = useOrderCart();
  const orgSettings = useOrgSettingsSlice();
  const { dynamicFormValues, setRequestFormValue } = useDynamicFormState();
  const shippingLocation =
    orderCart?.shipping_location?.price_modes?.includes(priceMode);
  const recipientLocations = withPrimaryLocation(
    dynamicFormValues?.requestForm?.company_locations
  );
  const showShippingLocation = showFormFirst
    ? Boolean(shippingLocation && recipientLocations.length > 0)
    : Boolean(shippingLocation);
  const shippingMethod =
    orderCart?.shipping_method?.price_modes?.includes(priceMode);
  const shippingInformation = orderCart?.shipping_information;
  const showShippingInformation =
    orderCart?.shipping_information?.price_modes?.includes(priceMode);
  const showPromo =
    getShowEditIcon(orderCart?.show_promo, priceMode) &&
    !!orgSettings.organization?.settings?.allow_coupon_code;

  const requestForm =
    formData.length > 0 ? (
      <>
        <RequestFormContainer>
          <DynamicForm
            form={formData}
            hideSubmitButton={true}
            minDate={new Date()}
            fromRequestDetail={false}
            keepExtraRequestKeys={showFormFirst && orderTypeFormData.length > 0}
            testIDPrefix={"order-now-jewelry-variation.request-form"}
          />
        </RequestFormContainer>
        <Spacer size={SpaceType.Big} />
      </>
    ) : null;

  return (
    <ChildContainer2>
      {showFormFirst && requestForm}
      {showShippingLocation && (
        <>
          <ShippingLocation
            title={t("orderCart.shippingLocation.title.text")}
            orderCart={orderCart}
            productColor={getProductColor(productType)}
            locations={showFormFirst ? recipientLocations : undefined}
            onLocationsChange={
              showFormFirst
                ? (locations) =>
                    setRequestFormValue("company_locations", locations)
                : undefined
            }
          />
          <Spacer size={SpaceType.Big} />
        </>
      )}
      {shippingMethod && (
        <>
          <ShippingMethod
            orderCart={orderCart}
            priceMode={priceMode}
            totalPrice={totalNetPrice}
          />
          <Spacer size={SpaceType.Big} />
        </>
      )}
      {theme.isTablet && <Spacer size={SpaceType.ExtraSmall} />}
      {!showFormFirst && requestForm}
      {showFormFirst && orderTypeFormData.length > 0 && (
        <>
          <RequestFormContainer>
            <DynamicForm
              form={orderTypeFormData}
              hideSubmitButton={true}
              minDate={new Date()}
              fromRequestDetail={false}
              mergeIntoRequestForm
              isolateRadioValue
              testIDPrefix="order-now-jewelry-variation.order-type"
            />
          </RequestFormContainer>
          <Spacer size={SpaceType.Big} />
        </>
      )}
      {showShippingInformation &&
        orgSettings?.organization?.custom_text
          ?.order_cart_shipping_instructions && (
          <>
            <ShippingInformation
              title={shippingInformation?.title}
              icon={shippingInformation?.icon}
              description={
                orgSettings?.organization?.custom_text
                  ?.order_cart_shipping_instructions
              }
              descriptionHeight={shippingInformation?.description_height}
            />
            <Spacer size={SpaceType.Big} />
          </>
        )}
      <RequestFormContainer>
        <CheckoutForm
          totalPrice={String(totalNetPrice)}
          itemList={[]}
          showPromo={showPromo}
          productType={productType as ProductCategory}
          setCouponCode={setCouponCode}
          refetchCartData={refetchCartData}
          cartData={cartData}
          couponCode={couponCode}
          cartAPIFetching={cartAPIFetching}
        />
      </RequestFormContainer>
    </ChildContainer2>
  );
};

const OrderNowBottomBar = ({
  totalNetPrice,
  shippingFee,
  promoDiscount = 0,
  formattedPrice,
  isAllConfigsComplete,
  selectedCount,
  selectedQuantity,
  pendingAction,
  formData,
  priceMode,
  onAddToCart,
  onPlaceOrder,
  onSendQuote,
  onIncompleteConfirm,
  isSendQuote = false,
}: {
  totalNetPrice: number;
  shippingFee: number;
  promoDiscount?: number;
  formattedPrice: (price: number) => string;
  isConfirmEvaluating: boolean;
  isAllConfigsComplete: boolean;
  selectedCount: number;
  selectedQuantity: number;
  pendingAction: "cart" | "order" | "quote" | null;
  formData: any[];
  priceMode: number;
  onAddToCart: () => void;
  onPlaceOrder: () => void;
  onSendQuote: () => void;
  onIncompleteConfirm?: () => void;
  isSendQuote?: boolean;
}) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const insets = useSafeAreaInsets();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [configTooltipName, setConfigTooltipName] = useState<string | null>(
    null
  );
  const [configTooltipMessage, setConfigTooltipMessage] = useState<string>("");
  const { isDynamicFormValid } = useDynamicFormState();
  const orderNowVariationCart = useOrderNowVariationCart();
  const sendQuoteOrderVariationCart = useSendQuoteOrderVariationCart();
  const isDisabled = selectedCount === 0 || pendingAction !== null;
  const isFormInvalid =
    formData.length > 0 && !(isDynamicFormValid as any)?.requestForm?.isValid;
  const addToCartMissingConfigMessage = {
    key: "orderNowVariation.configurationMissing.addToCart.text",
    defaultValue:
      "Please select all jewelry variations before adding the item(s) to the cart.",
  };
  const placeOrderMissingConfigMessage = {
    key: "orderNowVariation.configurationMissing.placeOrder.text",
    defaultValue: "All jewelry must be configured before the order",
  };
  const sendQuoteMissingConfigMessage = {
    key: "orderNowVariation.configurationMissing.sendQuote.text",
    defaultValue: "All jewelry must be configured before sending the quote",
  };

  const buttonConfig = isSendQuote
    ? sendQuoteOrderVariationCart
    : orderNowVariationCart;
  const bottomButtons = buttonConfig?.bottom_buttons ?? {};
  const buttonsArray = Object.keys(bottomButtons)
    .filter(
      (button) =>
        bottomButtons[button]?.price_modes?.length > 0 &&
        bottomButtons[button].price_modes.includes(priceMode)
    )
    .sort((a, b) => bottomButtons[a].order - bottomButtons[b].order)
    .map((button) => bottomButtons[button]);
  const orderedButtons =
    theme.isMobile && isSendQuote
      ? [...buttonsArray].sort((a, b) => {
          const rank = (name: string) =>
            name === "send_quote_order_cart" ? 0 : 1;
          return rank(a?.name) - rank(b?.name);
        })
      : buttonsArray;

  const getButtonAction = (name: string) => {
    if (name === "add_items_to_cart") return onAddToCart;
    if (name === "send_quote_order_cart") return onSendQuote;
    if (!isSendQuote && name === "place_order_cart") return onPlaceOrder;
    return undefined;
  };

  const getPendingKey = (name: string): "cart" | "order" | "quote" | null => {
    if (name === "add_items_to_cart") return "cart";
    if (name === "send_quote_order_cart") return "quote";
    if (!isSendQuote && name === "place_order_cart") return "order";
    return null;
  };

  const footerActionsRef = useRef<any>(null);
  const dismissConfigTooltip = useCallback(() => {
    setConfigTooltipName(null);
    setConfigTooltipMessage("");
  }, []);

  const handleFooterAction = (name: string) => {
    if (isDisabled) return;
    if (
      isSendQuote &&
      name !== "add_items_to_cart" &&
      name !== "send_quote_order_cart"
    )
      return;
    if (
      (name === "place_order_cart" || name === "send_quote_order_cart") &&
      isFormInvalid
    )
      return;
    if (!isAllConfigsComplete) {
      onIncompleteConfirm?.();
      const messageObject =
        name === "add_items_to_cart"
          ? addToCartMissingConfigMessage
          : name === "place_order_cart"
          ? placeOrderMissingConfigMessage
          : name === "send_quote_order_cart"
          ? sendQuoteMissingConfigMessage
          : null;
      if (!messageObject) return;
      setConfigTooltipName(name);
      setConfigTooltipMessage(
        t(messageObject.key, { defaultValue: messageObject.defaultValue })
      );
      return;
    }
    dismissConfigTooltip();
    getButtonAction(name)?.();
  };

  useEffect(() => {
    if (isAllConfigsComplete || selectedCount === 0) {
      dismissConfigTooltip();
    }
  }, [dismissConfigTooltip, isAllConfigsComplete, selectedCount]);

  useEffect(() => {
    if (!configTooltipName || Platform.OS !== "web") return;

    const handleClickOutside = (event: MouseEvent) => {
      const node = footerActionsRef.current as any;
      const domNode =
        node && typeof node.contains === "function"
          ? node
          : node?._nativeNode ?? node;
      if (domNode && typeof domNode.contains === "function") {
        if (domNode.contains(event.target as Node)) {
          return;
        }
      }
      dismissConfigTooltip();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [configTooltipName, dismissConfigTooltip]);

  const displayTotal = Math.max(
    0,
    (totalNetPrice ?? 0) + shippingFee - promoDiscount
  );

  const toolTipContent = (
    <ToolTipView>
      <VDBText
        color={theme["core-01"]}
        type={typography["Minor-R"]}
        textAlign={"left"}
      >
        {t("orderCart.checkout.tooltip.summary.text", "Summary:")}
      </VDBText>
      <Spacer size={SpaceType.Basic} />
      <ToolTipRow>
        <VDBText
          color={theme["core-01"]}
          type={typography["Minor-R"]}
          textAlign={"left"}
        >
          {t("orderCart.checkout.subTotal.text")}{" "}
        </VDBText>
        <VDBText color={theme["core-01"]} type={typography["Minor-R"]}>
          {formattedPrice(totalNetPrice ?? 0)}
        </VDBText>
      </ToolTipRow>
      {shippingFee > 0 && (
        <ToolTipRow>
          <VDBText color={theme["core-01"]} type={typography["Minor-R"]}>
            {t("shippingMethod.tooltip.label.text")}
          </VDBText>
          <VDBText color={theme["core-01"]} type={typography["Minor-M"]}>
            {formattedPrice(shippingFee)}
          </VDBText>
        </ToolTipRow>
      )}
      {promoDiscount > 0 && (
        <ToolTipRow>
          <VDBText color={theme["core-01"]} type={typography["Minor-R"]}>
            {t("orderCart.checkout.tooltip.discount.text")}{" "}
          </VDBText>
          <VDBText color={theme["core-01"]} type={typography["Minor-M"]}>
            {"-"}
            {formattedPrice(promoDiscount)}
          </VDBText>
        </ToolTipRow>
      )}
    </ToolTipView>
  );

  return (
    <>
      {configTooltipName != null && Platform.OS !== "web" ? (
        <RNModal
          transparent
          visible
          animationType="none"
          onRequestClose={dismissConfigTooltip}
        >
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={dismissConfigTooltip}
          />
        </RNModal>
      ) : null}
      <ButtonView insets={insets}>
        <ButtonContainer>
          <DisclaimerContainer>
            <TotalPriceRow>
              <TotalLabelRow>
                <ToolTipRow>
                  {theme.isLargeScreen ? (
                    <TooltipButton
                      children={
                        <VDBIcon
                          color={theme["core-06"]}
                          size={SpaceType.Big}
                          variant={IconsVariant.VDBCustom}
                          name={VDBIcons.INFO_COMMENT}
                        />
                      }
                      enableTooltip={true}
                      width="auto"
                      renderComponent={toolTipContent}
                    />
                  ) : (
                    <Tooltip
                      isOpen={tooltipIsOpen}
                      setIsOpen={setTooltipIsOpen}
                      trigger={() => (
                        <VDBIcon
                          color={theme["core-06"]}
                          size={SpaceType.Big}
                          variant={IconsVariant.VDBCustom}
                          name={VDBIcons.INFO_COMMENT}
                        />
                      )}
                      showOnPress={true}
                      tooltipBackgroundColor={theme["core-06"]}
                      popoverBackdropColor="transparent"
                      placement={"top"}
                      alignment={"flex-start"}
                      popoverContentProps={{ width: 240 }}
                    >
                      {toolTipContent}
                    </Tooltip>
                  )}
                </ToolTipRow>
                <VDBText color={theme["core-06"]} type={typography["H3-R"]}>
                  {t("orderCart.checkout.total.text")} ({selectedQuantity})
                </VDBText>
              </TotalLabelRow>
              <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                {totalNetPrice !== null
                  ? `${formattedPrice(displayTotal)}*`
                  : ""}
              </VDBText>
            </TotalPriceRow>
            <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
              {t("orderCart.checkout.message.text")}
            </VDBText>
          </DisclaimerContainer>
          <ButtonFlexContainer2 ref={footerActionsRef}>
            {orderedButtons.map((bottomButton: any) => {
              const pendingKey = getPendingKey(bottomButton?.name);
              const buttonName = bottomButton?.name;
              return (
                <ButtonSlot key={buttonName}>
                  {configTooltipName === buttonName ? (
                    <MissingConfigTooltip>
                      <MissingConfigTooltipBubble>
                        <VDBText
                          color={theme["core-01"]}
                          type={typography["Minor-R"]}
                          textAlign={"center"}
                        >
                          {configTooltipMessage}
                        </VDBText>
                      </MissingConfigTooltipBubble>
                      <MissingConfigTooltipArrow />
                    </MissingConfigTooltip>
                  ) : null}
                  <Button
                    category={ButtonCategory.default}
                    title={t(bottomButton?.label)}
                    type={bottomButton?.type ?? ButtonType.primary}
                    showIcon={!!bottomButton?.icon?.data}
                    iconSource={bottomButton?.icon?.data}
                    iconColor={theme["core-01"]}
                    iconSize={SpaceType.Big}
                    loading={pendingKey != null && pendingAction === pendingKey}
                    disabled={
                      isDisabled ||
                      ((buttonName === "place_order_cart" ||
                        buttonName === "send_quote_order_cart") &&
                        isFormInvalid)
                    }
                    onPress={() => handleFooterAction(buttonName)}
                  />
                </ButtonSlot>
              );
            })}
          </ButtonFlexContainer2>
        </ButtonContainer>
      </ButtonView>
    </>
  );
};

const OrderNowSelectionHeader = ({
  totalItems,
  selectedCount,
  allSelected,
  onToggleAll,
  onDeleteSelected,
}: {
  totalItems: number;
  selectedCount: number;
  allSelected: boolean;
  onToggleAll: () => void;
  onDeleteSelected: () => void;
}) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const rangeLabel = `${totalItems ? 1 : 0}-${totalItems}`;
  const totalLabel = currency(totalItems, {
    symbol: "",
    precision: 0,
  }).format();
  const selectAllText = t("searchResult.orderNow.selectAll.text", {
    x: rangeLabel,
    y: totalLabel,
    defaultValue: `Select <b>${rangeLabel}</b> out of <b>${totalLabel}</b> styles`,
  });
  const deSelectAllText = t("searchResult.orderNow.deSelectAll.text", {
    x: rangeLabel,
    y: totalLabel,
    defaultValue: `Deselect <b>${rangeLabel}</b> out of <b>${totalLabel}</b> styles`,
  });
  const canDelete = selectedCount > 0;

  return (
    <SelectionHeader>
      <SelectionLeft>
        <CheckBox
          fillColor={theme["knob-bor"]}
          checkBoxSize={20}
          isChecked={allSelected}
          checkedFunction={onToggleAll}
        />
        <RenderHTML
          contentWidth={280}
          source={{
            html: allSelected ? deSelectAllText : selectAllText,
          }}
          tagsStyles={{
            body: {
              ...typography["Body-R"],
              color: theme["core-06"],
            },
            b: {
              ...typography["Body-M"],
              color: theme["core-05"],
            },
          }}
        />
      </SelectionLeft>
      <SelectionDeleteButton
        onPress={onDeleteSelected}
        disabled={!canDelete}
        hitSlop={8}
      >
        <VDBText
          color={theme[canDelete ? "core-05" : "core-04"]}
          type={typography["Body-R"]}
        >
          {t("searchResult.deleteSelected.text", "Delete Selected")}
        </VDBText>
        <VDBIcon
          color={theme[canDelete ? "core-06" : "core-04"]}
          size={24}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.TRASH}
        />
      </SelectionDeleteButton>
    </SelectionHeader>
  );
};

const OrderNowJewelryVariationContent = ({
  mItem: mItemProp,
  mItems: mItemsProp,
  productType: productTypeProp,
  send_quote: sendQuoteProp,
}: OrderNowJewelryVariationScreenProps) => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const items: Diamond[] = useMemo(() => {
    const fromProp = mItemsProp?.length
      ? mItemsProp
      : mItemProp
      ? [mItemProp]
      : [];
    const fromRoute = route?.params?.mItems?.length
      ? route.params.mItems
      : route?.params?.mItem
      ? [route.params.mItem]
      : [];
    const raw = fromProp.length > 0 ? fromProp : fromRoute;
    return raw.map(normalizeItem);
  }, [mItemProp, mItemsProp, route?.params?.mItem, route?.params?.mItems]);
  const mItem = items[0];
  const productType: string =
    productTypeProp ?? route?.params?.productType ?? ProductCategory.JEWELRY;
  const isSendQuote =
    sendQuoteProp === true || route?.params?.send_quote === true;
  const theme = useTheme();
  const userInfo = useUserInfo();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const orderCart = useOrderCart();
  const { dynamicFormValues, isDynamicFormValid, requestClearRecipientInfo } =
    useDynamicFormState();
  const dispatch = useDispatch();
  const { shippingData } = useOrderCartSlice();
  const orderCheckout = useOrderCheckout();
  const {
    orderCartShippingInstructions,
    shoppingCartOfflinePaymentInstructions,
    settings,
  } = useOrgSettingsSlice();
  const companyLocations = useSelector(
    (state: any) => state.userInfo.companyLocations
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showQuoteSuccessPopup, setShowQuoteSuccessPopup] = useState(false);
  const [quoteSuccessRequest, setQuoteSuccessRequest] = useState<any>(null);
  const quoteSuccessRequestRef = useRef<any>(null);
  const { shareContent } = useShareContent();
  const [canRequestModal, setCanRequestModal] = useState(false);
  const [showPlaceOrderConfirm, setShowPlaceOrderConfirm] = useState(false);
  const [showSendQuoteConfirm, setShowSendQuoteConfirm] = useState(false);
  const [removeConfirmPopup, setRemoveConfirmPopup] = useState<{
    openPopup: boolean;
    ids: number[];
  }>({
    openPopup: false,
    ids: [],
  });
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [couponCode, setCouponCodeState] = useState("");
  const [promoCartData, setPromoCartData] = useState<any>(undefined);
  const [promoFetching, setPromoFetching] = useState(false);
  const couponCodeRef = useRef("");
  const promoSessionRef = useRef<{ id: string; signature: string } | null>(
    null
  );
  const allowLeaveRef = useRef(false);
  const screenFocusedRef = useRef(false);
  const pendingLeaveActionRef = useRef<any>(null);

  const cancelLeave = useCallback(() => {
    pendingLeaveActionRef.current = null;
    OrderNowLeaveGuard.cancel();
    setShowLeaveConfirm(false);
  }, []);

  const confirmLeave = useCallback(() => {
    allowLeaveRef.current = true;
    OrderNowLeaveGuard.setActive(false);
    setShowLeaveConfirm(false);
    const action = pendingLeaveActionRef.current;
    pendingLeaveActionRef.current = null;
    if (action) {
      OrderNowLeaveGuard.cancel();
      navigation.dispatch(action);
    } else {
      OrderNowLeaveGuard.confirm();
    }
  }, [navigation]);

  useFocusEffect(
    useCallback(() => {
      screenFocusedRef.current = true;
      allowLeaveRef.current = false;
      OrderNowLeaveGuard.setActive(true);
      OrderNowLeaveGuard.register(
        () => setShowLeaveConfirm(true),
        () => setShowLeaveConfirm(false)
      );
      return () => {
        screenFocusedRef.current = false;
        OrderNowLeaveGuard.setActive(false);
      };
    }, [])
  );

  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", (e: any) => {
      const allowLeave = allowLeaveRef.current;
      const bypass = OrderNowLeaveGuard.consumeRemovalBypass();
      const screenFocused = screenFocusedRef.current;
      if (!screenFocused || allowLeave || bypass) {
        return;
      }
      e.preventDefault();
      pendingLeaveActionRef.current = e.data.action;
      setShowLeaveConfirm(true);
    });
    return unsubscribe;
  }, [navigation]);

  useEffect(() => {
    if (navigation?.setOptions) {
      const bgColorKey = getProductColor(productType);
      const tintColorKey = getProductTintColor(productType);
      navigation.setOptions({
        headerShown: !theme.isLargeScreen,
        headerStyle: {
          backgroundColor: theme[bgColorKey],
        },
        headerTintColor: theme[tintColorKey],
        headerLeft: () => <GetHeaderLeft canGoBack textColor={tintColorKey} />,
      });
    }
  }, [navigation, theme, productType]);
  const priceMode: number = (userInfo as any)?.user?.price_mode ?? 1;
  const personalizationSectionLayout = useGetPdpSectionLayout(
    PdpSections.PERSONALIZATION,
    productType
  );
  const pdpSectionsConfig = useGetPdpSectionsOrder(productType);
  const visiblePdpSections = useMemo(
    () => getVisiblePdpSections(pdpSectionsConfig, priceMode),
    [pdpSectionsConfig, priceMode]
  );
  const showPersonalizationSectionOnPdp = useSelector(
    (state: any) =>
      state.globalData?.app_settings
        ?.show_personalization_section_on_pdp_screen ?? false
  );
  const isPersonalizationSectionEnabled =
    visiblePdpSections.includes(PdpComponentNames.PERSONALIZATION) &&
    (personalizationSectionLayout?.price_modes?.includes(priceMode) ?? false) &&
    showPersonalizationSectionOnPdp &&
    (userInfo?.isGuest
      ? (personalizationSectionLayout as any)?.restrict_public_user === false
      : true);
  const bulkActionsCall = useBulkActions();
  const queryClient = useQueryClient();
  const { unifiedProductData } = useUnifiedProductPagesSlice();
  const quoteForm =
    unifiedProductData?.[productType]?.bulkList?.bulkInit?.formData?.[
      RequestType.QUOTE
    ];
  const additionalForm = orderCart?.hide_additional_form
    ? {}
    : orderCart?.additional_details_form ?? {};
  const includeOrderTypeWithQuote =
    isSendQuote &&
    userInfo.enableOrderCartQuote() === true &&
    !orderCart?.hide_additional_form;
  const formData = useMemo(() => {
    return (
      filterAndSortFormData(
        isSendQuote ? quoteForm : additionalForm,
        priceMode
      ) ?? []
    );
  }, [additionalForm, isSendQuote, priceMode, quoteForm]);
  const orderTypeFormData = useMemo(() => {
    if (!includeOrderTypeWithQuote) return [];
    const fields = filterAndSortFormData(additionalForm, priceMode) ?? [];
    return fields.map((field: any) =>
      field?.type === "radioSelect" && field?.name === "radioSelect"
        ? { ...field, name: "orderType" }
        : field
    );
  }, [additionalForm, includeOrderTypeWithQuote, priceMode]);

  const [isApiLoading, setIsApiLoading] = useState(false);
  const [showConfigErrors, setShowConfigErrors] = useState(false);
  const startLoading = () => setIsApiLoading(true);
  const stopLoading = () => setIsApiLoading(false);

  const [configs, setConfigs] = useState<ConfigEntry[]>(() =>
    items.map((item, index) => ({
      id: index,
      baseItem: item,
      displayConfiguration: isConfigurableJewelryItem(item)
        ? cloneDisplayConfig(item.display_configuration_v2 ?? [])
        : [],
    }))
  );
  const [selectedCellIds, setSelectedCellIds] = useState<number[]>(() =>
    items.map((_, index) => index)
  );
  const [pendingConfirmAction, setPendingConfirmAction] = useState<
    "cart" | "order" | "quote" | null
  >(null);
  const [cellSelections, setCellSelections] = useState<
    Record<number, Record<string, string>>
  >(() => {
    const seeded: Record<number, Record<string, string>> = {};
    items.forEach((item, index) => {
      const selections = seedSelectionsFromItem(item);
      if (Object.keys(selections).length > 0) {
        seeded[index] = selections;
      }
    });
    return seeded;
  });
  const [cellPersonalization, setCellPersonalization] = useState<
    Record<number, Record<string, any>>
  >({});
  const [cellComments, setCellComments] = useState<Record<number, string>>({});

  // Keyed by option_name → label; used only as a stable cache key.
  const [activeVariantParams, setActiveVariantParams] = useState<Record<
    string,
    string
  > | null>(null);

  // The actual query params sent to the API (slug_details[option_type] format).
  const [activeApiParams, setActiveApiParams] = useState<Record<
    string,
    string
  > | null>(null);

  // Stores the selections state of a cell just before a user-triggered API
  // call so we can revert if the combination comes back empty.
  const prevCellSelectionsRef = useRef<{
    cellId: number;
    selections: Record<string, string>;
    variantParams: Record<string, string> | null;
    apiParams: Record<string, string> | null;
  } | null>(null);

  // When true, onSettled will skip calling onClose() so the modal stays open
  // and the user can see the error toast rendered inside the modal.
  const skipCloseRef = useRef(false);
  const nextCellIdRef = useRef(items.length);
  const cellSelectionsRef = useRef(cellSelections);
  cellSelectionsRef.current = cellSelections;
  const lastVariationByProductRef = useRef<
    Record<number, { key: string; data: JewelryVariation }>
  >({});
  const lastProcessedVariationRef = useRef<{
    cellId: number;
    processKey: string;
  } | null>(null);

  const [cellInfo, setCellInfo] = useState<Record<number, CellInfo>>(() => {
    const seeded: Record<number, CellInfo> = {};
    items.forEach((item, index) => {
      seeded[index] = seedCellInfoFromItem(item);
    });
    return seeded;
  });

  const [activeCellId, setActiveCellId] = useState<number>(0);
  const activeBaseItem =
    configs.find((config) => config.id === activeCellId)?.baseItem ?? mItem;

  const selectedConfigs = configs.filter((cell) =>
    selectedCellIds.includes(cell.id)
  );

  const computedNetPrice = selectedConfigs.reduce((sum, cell) => {
    const meta = getVariationMeta(cell.variationData);
    const quantity = cellInfo[cell.id]?.quantity ?? 1;
    if (meta.price != null) return sum + meta.price * quantity;
    return sum + (cellInfo[cell.id]?.totalPrice ?? 0);
  }, 0);
  const fallbackPrice = selectedConfigs.reduce(
    (sum, cell) => sum + (Number(cell.baseItem?.total_sales_price) || 0),
    0
  );
  // Before any selected cell reports its price (e.g. right after mount) the
  // computed total is 0; fall back to those items' total_sales_price.
  const totalNetPrice =
    computedNetPrice === 0 ? fallbackPrice : computedNetPrice;
  const shippingMethodEnabled =
    !!orderCart?.shipping_method?.price_modes?.includes(priceMode);
  const availableShippingMethods = useMemo(
    () =>
      getAvailableShippingMethods(
        shippingData?.shippingMethods as unknown as FeeResponseItem[],
        totalNetPrice
      ),
    [shippingData?.shippingMethods, totalNetPrice]
  );
  const shippingFee =
    shippingMethodEnabled &&
    shippingData?.selectedItem?.flat_fee &&
    availableShippingMethods.length > 0
      ? Number(shippingData.selectedItem.flat_fee)
      : 0;
  const selectedQuantity = selectedConfigs.reduce(
    (sum, cell) => sum + (cellInfo[cell.id]?.quantity ?? 1),
    0
  );
  const screenTitle = `${t(
    isSendQuote
      ? "orderNowModal.quoteList.title.text"
      : "orderNowModal.orderNow.title.text",
    isSendQuote ? "Quote list" : "Order Now"
  )} (${selectedQuantity})`;

  const HeaderTitleContainer = useCallback(
    () => (
      <RowContainer>
        <VDBText color={theme["core-05"]} type={typography["H2-M"]}>
          {screenTitle}
        </VDBText>
      </RowContainer>
    ),
    [theme, typography, screenTitle]
  );

  useEffect(() => {
    if (navigation?.setOptions) {
      navigation.setOptions({ title: screenTitle });
    }
  }, [navigation, screenTitle]);

  const getCellItem = (cellId: number): Diamond => {
    const config = configs.find((c) => c.id === cellId);
    const fromVariation = getVariationItem(config?.variationData);
    const base = fromVariation ?? config?.baseItem ?? mItem;
    if (!config?.displayConfiguration) return base;
    return {
      ...base,
      display_configuration_v2: config.displayConfiguration,
    };
  };

  const getCellCompleteness = (cell: ConfigEntry) => {
    const displayConfig =
      cell.displayConfiguration ??
      cell.baseItem?.display_configuration_v2 ??
      [];
    const requiredOptions = displayConfig.filter(isOptionRequiredForConfirm);
    const selections = cellSelections[cell.id] ?? {};
    const missingOptions = requiredOptions
      .filter((option) => !hasMatchingOptionSelection(option, selections))
      .map((option) => option.option_name);
    const meta = getVariationMeta(cell.variationData);
    const cartItemId = resolveCartItemId(cell, cellInfo[cell.id]);
    const displayItem = getCellItem(cell.id);
    const personalizationShown =
      isPersonalizationSectionEnabled &&
      !!displayItem?.custom_design_configuration;
    const personalizationOk =
      !personalizationShown ||
      isEngravePairComplete(cellPersonalization[cell.id]);
    return {
      cellId: cell.id,
      optionsOk: missingOptions.length === 0,
      missingOptions,
      requiredOptions: requiredOptions.map((option) => option.option_name),
      selections,
      variantId: cartItemId,
      hasVariationData: !!cell.variationData,
      variationDataId: meta.firstVariation?.data?.id ?? null,
      variationItemId: meta.firstVariation?.item?.id ?? null,
      personalizationOk,
      complete:
        missingOptions.length === 0 && cartItemId != null && personalizationOk,
    };
  };

  const isAllConfigsComplete = selectedConfigs.every(
    (cell) => getCellCompleteness(cell).complete
  );

  useEffect(() => {
    if (isAllConfigsComplete) {
      setShowConfigErrors(false);
    }
  }, [isAllConfigsComplete]);

  const activeVariantKey = activeVariantParams
    ? makeVariantKey(activeVariantParams)
    : "";

  const {
    data: variationData,
    isFetching: isVariationFetching,
    isError: isVariationError,
  } = useGetJewelryVariation(
    activeBaseItem?.id as number,
    priceMode,
    1,
    !!activeBaseItem?.id && !!activeApiParams,
    activeApiParams ?? undefined
  );

  const activeConfig = configs.find((config) => config.id === activeCellId);
  const incomingVariantId = getVariationMeta(variationData).variantId;
  const appliedVariantId = getVariationMeta(
    activeConfig?.variationData
  ).variantId;
  const hasUnappliedVariation =
    incomingVariantId != null && incomingVariantId !== appliedVariantId;
  const isConfirmEvaluating =
    isApiLoading || isVariationFetching || hasUnappliedVariation;

  useEffect(() => {
    if (isVariationFetching || !activeApiParams) {
      return;
    }

    if (isVariationError) {
      stopLoading();
      return;
    }

    const jewelryData = unwrapJewelryVariation(variationData);
    if (!jewelryData) {
      if (variationData) {
        stopLoading();
      }
      return;
    }

    const variations = jewelryData?.response?.body?.variations;

    if (variations && variations.length === 0) {
      const isUserChange = !!prevCellSelectionsRef.current;
      if (isUserChange) {
        const previous = prevCellSelectionsRef.current!;
        prevCellSelectionsRef.current = null;

        setCellSelections((prev) => ({
          ...prev,
          [previous.cellId]: previous.selections,
        }));
        setActiveVariantParams(previous.variantParams);
        setActiveApiParams(previous.apiParams);
        showToast(
          "",
          t(
            "searchResult.jewelry.combinationNotFound.text",
            "This combination is not available. Please change your selection."
          ),
          ToastTypes.ERROR,
          true,
          true,
          80
        );
      }
      stopLoading();
      return;
    }

    const processKey = `${activeBaseItem?.id ?? ""}:${activeVariantKey}`;
    const lastProcessed = lastProcessedVariationRef.current;
    const activeHasVariation = !!configs.find(
      (config) => config.id === activeCellId
    )?.variationData;
    if (
      lastProcessed?.processKey === processKey &&
      lastProcessed?.cellId !== activeCellId &&
      activeHasVariation
    ) {
      lastProcessedVariationRef.current = {
        cellId: activeCellId,
        processKey,
      };
      stopLoading();
      return;
    }
    lastProcessedVariationRef.current = {
      cellId: activeCellId,
      processKey,
    };

    const responseKey = canonicalVariantKey(
      activeVariantParams ?? undefined,
      activeBaseItem?.display_configuration_v2 ?? []
    );
    if (activeBaseItem?.id != null && responseKey) {
      lastVariationByProductRef.current[Number(activeBaseItem.id)] = {
        key: responseKey,
        data: cloneVariationData(jewelryData) as JewelryVariation,
      };
    }
    setConfigs((prev) =>
      prev.map((config) =>
        config.id === activeCellId
          ? applyVariationToCell(config, jewelryData)
          : config
      )
    );

    setCellInfo((prev) => {
      const nextInfo = cellInfoFromVariation(
        jewelryData,
        prev[activeCellId]?.quantity ?? 1,
        prev[activeCellId]?.totalPrice ?? null
      );
      if (!nextInfo) return prev;
      return {
        ...prev,
        [activeCellId]: nextInfo,
      };
    });

    setCellSelections((prev) => ({
      ...prev,
      [activeCellId]: mergeSelectionsFromVariation(
        prev[activeCellId] ?? {},
        jewelryData
      ),
    }));

    stopLoading();
  }, [
    variationData,
    activeVariantKey,
    activeCellId,
    activeBaseItem?.id,
    activeApiParams,
    isVariationFetching,
    isVariationError,
  ]);

  const addNewConfiguration = (sourceConfig: ConfigEntry) => {
    const newId = nextCellIdRef.current++;
    const sourceSelections = {
      ...(cellSelectionsRef.current[sourceConfig.id] ?? {}),
    };
    const clonedVariation = cloneVariationData(sourceConfig.variationData);
    const seedMeta = getVariationMeta(clonedVariation);
    const sourceInfo = cellInfo[sourceConfig.id];
    const unitPrice =
      seedMeta.price ??
      (sourceInfo?.quantity
        ? (sourceInfo.totalPrice ?? 0) / sourceInfo.quantity
        : sourceInfo?.totalPrice ?? null);

    const nextConfig: ConfigEntry = {
      id: newId,
      baseItem: sourceConfig.baseItem,
      displayConfiguration: cloneDisplayConfig(
        sourceConfig.displayConfiguration
      ),
      ...(clonedVariation ? { variationData: clonedVariation } : {}),
    };
    setConfigs((prev) => {
      const index = prev.findIndex((config) => config.id === sourceConfig.id);
      if (index === -1) return [...prev, nextConfig];
      return [
        ...prev.slice(0, index + 1),
        nextConfig,
        ...prev.slice(index + 1),
      ];
    });
    const nextSelections = {
      ...cellSelectionsRef.current,
      [newId]: sourceSelections,
    };
    cellSelectionsRef.current = nextSelections;
    setCellSelections(nextSelections);
    setCellInfo((prev) => ({
      ...prev,
      [newId]: {
        quantity: 1,
        variantId:
          seedMeta.variantId ??
          sourceInfo?.variantId ??
          parseItemId(sourceConfig.baseItem?.id),
        totalPrice:
          unitPrice ?? seedCellInfoFromItem(sourceConfig.baseItem).totalPrice,
      },
    }));
    setSelectedCellIds((prev) => {
      if (prev.includes(newId)) return prev;
      const index = prev.indexOf(sourceConfig.id);
      if (index === -1) return [...prev, newId];
      return [...prev.slice(0, index + 1), newId, ...prev.slice(index + 1)];
    });
  };

  const handleSelectionChange = (
    cellId: number,
    optionName: string,
    value: string
  ) => {
    if (optionName === "quantity") return;
    if (cellSelectionsRef.current[cellId]?.[optionName] === value) return;
    setActiveCellId(cellId);
    setCellSelections((prev) => {
      const updatedCell = { ...(prev[cellId] ?? {}), [optionName]: value };
      const updatedAll = { ...prev, [cellId]: updatedCell };

      const cellConfig = configs.find((config) => config.id === cellId);
      const displayConfig =
        cellConfig?.displayConfiguration ??
        getCellItem(cellId)?.display_configuration_v2 ??
        [];
      const slugOptions = displayConfig.filter((option) => option.option_slug);
      const changedSlugOption = slugOptions.find(
        (option) => option.option_name === optionName
      );
      const allSelected = slugOptions.every(
        (option) => !!updatedCell[option.option_name]
      );
      const nextApiParams =
        allSelected && changedSlugOption
          ? buildApiParams(updatedCell, displayConfig, optionName)
          : null;
      const paramsUnchanged =
        !!nextApiParams &&
        JSON.stringify(activeApiParams) === JSON.stringify(nextApiParams);

      if (nextApiParams) {
        if (paramsUnchanged) {
          return updatedAll;
        }
        const slugOnlyCell: Record<string, string> = {};
        displayConfig.forEach((configItem) => {
          if (configItem.option_slug && updatedCell[configItem.option_name]) {
            slugOnlyCell[configItem.option_name] =
              updatedCell[configItem.option_name];
          }
        });
        prevCellSelectionsRef.current = {
          cellId,
          selections: prev[cellId] ?? {},
          variantParams: activeVariantParams,
          apiParams: activeApiParams,
        };
        startLoading();
        setActiveVariantParams(slugOnlyCell);
        setActiveApiParams(nextApiParams);
      }

      return updatedAll;
    });
  };

  const formattedPrice = (mPrice: number) => {
    const totalValue = currency(mPrice, {
      symbol: userInfo?.user?.currency_symbol,
      precision: 0,
    }).format();
    return totalValue;
  };

  const removeConfigurations = (ids: number[]) => {
    if (!ids.length) return;
    const idSet = new Set(ids);
    const next = configs.filter((config) => !idSet.has(config.id));
    setConfigs(next);
    setSelectedCellIds((prev) => prev.filter((id) => !idSet.has(id)));
    setCellSelections((prev) => {
      const updated = { ...prev };
      ids.forEach((id) => {
        delete updated[id];
      });
      return updated;
    });
    setCellInfo((prev) => {
      const updated = { ...prev };
      ids.forEach((id) => {
        delete updated[id];
      });
      return updated;
    });
    setCellPersonalization((prev) => {
      const updated = { ...prev };
      ids.forEach((id) => {
        delete updated[id];
      });
      return updated;
    });
    setCellComments((prev) => {
      const updated = { ...prev };
      ids.forEach((id) => {
        delete updated[id];
      });
      return updated;
    });
    if (next.length === 0) {
      stopLoading();
      return;
    }
    if (idSet.has(activeCellId)) {
      setActiveCellId(next[0].id);
    }
  };

  const requestRemoveConfigurations = (ids: number[]) => {
    if (!ids.length) return;
    setRemoveConfirmPopup({
      openPopup: true,
      ids,
    });
  };

  const closeRemoveConfirmPopup = () => {
    setRemoveConfirmPopup({
      openPopup: false,
      ids: [],
    });
  };

  const confirmRemoveConfigurations = () => {
    const ids = removeConfirmPopup.ids;
    closeRemoveConfirmPopup();
    removeConfigurations(ids);
  };

  const toggleCellSelection = (cellId: number, checked: boolean) => {
    setSelectedCellIds((prev) => {
      if (checked) {
        return prev.includes(cellId) ? prev : [...prev, cellId];
      }
      return prev.filter((id) => id !== cellId);
    });
  };

  const allCellsSelected =
    configs.length > 0 && selectedCellIds.length === configs.length;

  const toggleSelectAll = () => {
    setSelectedCellIds(
      allCellsSelected ? [] : configs.map((config) => config.id)
    );
  };

  const promoResponse = promoCartData?.data?.response;
  const promoDiscount = promoResponse?.coupon
    ? Math.max(
        0,
        Number(promoResponse.old_total_cart_price ?? 0) -
          Number(promoResponse.total_cart_price ?? 0)
      )
    : 0;
  const promoCodeLabel = promoResponse?.coupon?.coupon_code as
    | string
    | undefined;

  const buildPlaceOrderParams = (buyNowSessionId: string) => {
    const formValues = dynamicFormValues.requestForm;
    const filteredFormValues = Object.fromEntries(
      Object.entries(formValues ?? {}).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ""
      )
    );
    const commentFormValues =
      includeOrderTypeWithQuote && priceMode === PriceModes.WHITE
        ? omitQuoteRecipientCommentFields(filteredFormValues)
        : filteredFormValues;
    const additionalDetails = formatPersonalisationText(
      commentFormValues,
      (dynamicFormValues as any).comments,
      includeOrderTypeWithQuote ? quoteForm : additionalForm,
      includeOrderTypeWithQuote
        ? {
            orderTypeForm: orderCart?.additional_details_form,
            translate: (key, fallback) => t(key, fallback ?? key),
          }
        : undefined
    );
    const shippingLocationEnabled =
      orderCart?.shipping_location?.price_modes?.includes(priceMode);
    const showShippingInformation =
      orderCart?.shipping_information?.price_modes?.includes(priceMode);
    const checkoutTotal = Math.max(
      0,
      totalNetPrice + shippingFee - promoDiscount
    );

    return {
      order_comments: additionalDetails ?? "",
      price_mode: priceMode,
      total_price: checkoutTotal,
      ...(buyNowSessionId && {
        buy_now_session_id: buyNowSessionId,
      }),
      ...(shippingMethodEnabled &&
        shippingData?.selectedItem?.id && {
          shipping_method: shippingData.selectedItem.id,
          cart_subtotal: totalNetPrice,
          front_calculation_price: shippingFee,
        }),
      ...(shippingLocationEnabled && {
        shipping_location: filterLocationData(
          orderCart?.shipping_location,
          getPrimaryLocation({ locations: companyLocations || [] } as any),
          priceMode,
          t
        ),
      }),
      ...(showShippingInformation &&
        orderCartShippingInstructions && { shipping_instructions: true }),
      ...(shoppingCartOfflinePaymentInstructions && {
        wire_transfer_details: true,
      }),
      ...(promoDiscount > 0 &&
        promoCodeLabel && {
          promo_discount_amount: promoDiscount,
          promo_code_label: promoCodeLabel,
        }),
    };
  };

  const normalizeShareVia = (value: unknown): "email" | "whatsapp" => {
    const raw = String(value ?? "").toLowerCase();
    return raw.includes("whats") ? "whatsapp" : "email";
  };

  const buildQuoteCheckoutParams = (buyNowSessionId: string) => {
    const formValues = dynamicFormValues?.requestForm ?? {};
    const shareVia = normalizeShareVia(formValues.radioSelect);
    const isWhatsapp = shareVia === "whatsapp";
    const shippingLocationEnabled =
      orderCart?.shipping_location?.price_modes?.includes(priceMode);
    const recipientLocations = withPrimaryLocation(
      formValues.company_locations
    );
    const placeOrderParams = buildPlaceOrderParams(buyNowSessionId);
    const quoteBaseParams = { ...placeOrderParams };
    delete quoteBaseParams.shipping_location;

    return {
      ...quoteBaseParams,
      ...(shippingLocationEnabled &&
        recipientLocations.length > 0 && {
          shipping_location: filterLocationData(
            orderCart?.shipping_location,
            getPrimaryLocation({ locations: recipientLocations } as any),
            priceMode,
            t
          ),
        }),
      order_type: "quote",
      share_via: shareVia,
      recipient_name: formValues.recipientName,
      ...(shareVia === "email" &&
        formValues.recipientEmail && {
          recipient_email: formValues.recipientEmail,
        }),
      ...(isWhatsapp &&
        formValues.phoneNumber && {
          recipient_phone_number: formValues.phoneNumber,
        }),
      ...(isWhatsapp &&
        formValues.countryCode && {
          recipient_phone_country_code: String(formValues.countryCode),
        }),
      ...(formValues.recipientId && {
        recipient_id: formValues.recipientId,
      }),
      ...(formValues.recipient_company_name && {
        recipient_company_name: formValues.recipient_company_name,
      }),
      ...(formValues.recipient_company_location && {
        recipient_company_location: formValues.recipient_company_location,
      }),
    };
  };

  const placeOrderCheckout = (buyNowSessionId: string) => {
    const params = buildPlaceOrderParams(buyNowSessionId);
    orderCheckout.mutate(params, {
      onSuccess() {
        skipCloseRef.current = true;
        allowLeaveRef.current = true;
        OrderNowLeaveGuard.setActive(false);
        queryClient.invalidateQueries({ queryKey: ["GET_CART_ITEM"] }).then();
        queryClient.invalidateQueries({ queryKey: ["stock_item_request"] });
        queryClient.invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] });
        setShowSuccessModal(true);
      },
      onError() {
        skipCloseRef.current = true;
        showToast(
          t("orderCart.offline.error.title", "Order Failed"),
          t(
            "orderCart.offline.error.message",
            "Failed to create your order. Please try again."
          ),
          ToastTypes.ERROR,
          true,
          true
        );
      },
      onSettled() {
        setPendingConfirmAction(null);
        stopLoading();
      },
    });
  };

  const placeQuoteCheckout = (buyNowSessionId: string) => {
    const params = buildQuoteCheckoutParams(buyNowSessionId);
    orderCheckout.mutate(params, {
      onSuccess(data) {
        skipCloseRef.current = true;
        allowLeaveRef.current = true;
        OrderNowLeaveGuard.setActive(false);
        queryClient.invalidateQueries({ queryKey: ["GET_CART_ITEM"] }).then();
        queryClient.invalidateQueries({ queryKey: ["stock_item_request"] });
        queryClient.invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] });
        const root = data?.data ?? data;
        const order =
          root?.response?.body?.result?.order ??
          root?.response?.result?.order ??
          root?.response?.order ??
          root?.body?.result?.order ??
          root?.result?.order ??
          root?.order;
        const quoteRequest = order?.id
          ? {
              ...order,
              id: order.id,
              request_type: order.order_type ?? "quote",
            }
          : null;
        quoteSuccessRequestRef.current = quoteRequest;
        setQuoteSuccessRequest(quoteRequest);
        const shareVia = normalizeShareVia(
          dynamicFormValues?.requestForm?.radioSelect
        );
        const openQuoteSuccess = () => setShowQuoteSuccessPopup(true);
        if (shareVia === "whatsapp" && quoteRequest) {
          shareContent(quoteRequest, false, false, true, NativeShare).finally(
            openQuoteSuccess
          );
          return;
        }
        openQuoteSuccess();
      },
      onError() {
        skipCloseRef.current = true;
        showToast(
          t("orderCart.offline.error.title", "Order Failed"),
          t(
            "orderNowVariation.sendQuote.error.message",
            "Failed to send your quote. Please try again."
          ),
          ToastTypes.ERROR,
          true,
          true
        );
      },
      onSettled() {
        setPendingConfirmAction(null);
        stopLoading();
      },
    });
  };

  const requestPlaceOrder = () => {
    const isFormInvalid =
      formData.length > 0 && !(isDynamicFormValid as any)?.requestForm?.isValid;
    if (!selectedConfigs.length || !isAllConfigsComplete || isFormInvalid)
      return;
    if (!userInfo.canRequest()) {
      setCanRequestModal(true);
      return;
    }
    setShowPlaceOrderConfirm(true);
  };

  const requestSendQuote = () => {
    const isFormInvalid =
      formData.length > 0 && !(isDynamicFormValid as any)?.requestForm?.isValid;
    if (!selectedConfigs.length || !isAllConfigsComplete || isFormInvalid)
      return;
    if (!userInfo.canRequest()) {
      setCanRequestModal(true);
      return;
    }
    setShowSendQuoteConfirm(true);
  };

  const collectSelectedCartLines = () => {
    const itemIds: number[] = [];
    const itemCartAttributes: Record<string, OrderCartLine | OrderCartLine[]> =
      {};

    selectedConfigs.forEach((cell) => {
      const info = cellInfo[cell.id];
      const selections = cellSelections[cell.id];
      const cartItemId = resolveCartItemId(cell, info);
      const displayConfig =
        cell.displayConfiguration ??
        getCellItem(cell.id)?.display_configuration_v2 ??
        [];

      if (cartItemId != null) {
        const key = String(cartItemId);
        const itemComments = isSendQuote
          ? cellComments[cell.id]?.trim() ?? ""
          : "";
        const line: OrderCartLine = {
          quantity: info?.quantity ?? 1,
          config: selections
            ? buildConfigFromSelections(selections, displayConfig)
            : {},
          personalization: compactPersonalization(cellPersonalization[cell.id]),
          ...(itemComments ? { item_comments: itemComments } : {}),
        };
        const existing = itemCartAttributes[key];

        if (!existing) {
          itemIds.push(cartItemId);
          itemCartAttributes[key] = line;
          return;
        }

        const signature = cartLineSignature(line);
        if (Array.isArray(existing)) {
          const match = existing.find(
            (entry) => cartLineSignature(entry) === signature
          );
          if (match) {
            match.quantity += line.quantity;
          } else {
            existing.push(line);
          }
          return;
        }

        if (cartLineSignature(existing) === signature) {
          existing.quantity += line.quantity;
          return;
        }

        itemCartAttributes[key] = [existing, line];
      }
    });

    return {
      itemIds,
      itemCartAttributes,
      signature: JSON.stringify({ itemIds, itemCartAttributes }),
    };
  };

  const selectedCartSignature = collectSelectedCartLines().signature;

  useEffect(() => {
    if (
      promoSessionRef.current &&
      promoSessionRef.current.signature !== selectedCartSignature
    ) {
      promoSessionRef.current = null;
      setPromoCartData(undefined);
    }
  }, [selectedCartSignature]);

  const setCouponCode = (code: string) => {
    const next = typeof code === "string" ? code : "";
    couponCodeRef.current = next;
    setCouponCodeState(next);
  };

  const refetchPromoCart = async () => {
    const code = couponCodeRef.current?.trim();
    if (!code) return;
    const { itemIds, itemCartAttributes, signature } =
      collectSelectedCartLines();
    if (!itemIds.length) return;

    setPromoFetching(true);
    startLoading();
    try {
      let sessionId =
        promoSessionRef.current?.signature === signature
          ? promoSessionRef.current.id
          : null;
      if (!sessionId) {
        const payload = {
          action_type: BulkActionsEnum.BULK_REQUEST_ACTION,
          stock_type: getStockItemType(ProductCategory.JEWELRY),
          cart_type: CartTypeEnum.ORDER_CART,
          item_ids: itemIds,
          price_mode: priceMode,
          item_cart_attributes: itemCartAttributes,
          buy_now: true,
        };
        const data = await bulkActionsCall.mutateAsync(payload);
        sessionId = data?.data?.response?.body?.buy_now_session_id;
        if (!sessionId) {
          showToast(
            t("orderCart.offline.error.title", "Order Failed"),
            t(
              "orderCart.offline.error.message",
              "Failed to create your order. Please try again."
            ),
            ToastTypes.ERROR,
            true,
            true
          );
          return;
        }
        promoSessionRef.current = { id: sessionId, signature };
      }

      const response = await apiClient.get(ApiRoutes.GET_CART_ITEMS, {
        params: {
          stock_type: getStockItemType(ProductCategory.JEWELRY),
          cart_type: CartTypeEnum.ORDER_CART,
          price_mode: priceMode,
          buy_now_session_id: sessionId,
          coupon_code: code,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      });
      setPromoCartData({
        data: response.data,
        config: { params: { coupon_code: code } },
      });
    } catch {
      showToast(
        t("orderCart.offline.error.title", "Order Failed"),
        t(
          "orderCart.offline.error.message",
          "Failed to create your order. Please try again."
        ),
        ToastTypes.ERROR,
        true,
        true
      );
    } finally {
      setPromoFetching(false);
      stopLoading();
    }
  };

  const confirmOrder = (mode: "cart" | "order" | "quote") => {
    const buyNow = mode !== "cart";
    if (!selectedConfigs.length || !isAllConfigsComplete) return;
    if (buyNow && !userInfo.canRequest()) {
      setCanRequestModal(true);
      return;
    }

    const { itemIds, itemCartAttributes, signature } =
      collectSelectedCartLines();
    const existingSessionId =
      buyNow && promoSessionRef.current?.signature === signature
        ? promoSessionRef.current.id
        : null;

    if (buyNow && existingSessionId) {
      skipCloseRef.current = true;
      setPendingConfirmAction(mode);
      startLoading();
      if (mode === "quote") {
        placeQuoteCheckout(existingSessionId);
      } else {
        placeOrderCheckout(existingSessionId);
      }
      return;
    }

    const payload = {
      action_type: BulkActionsEnum.BULK_REQUEST_ACTION,
      stock_type: getStockItemType(ProductCategory.JEWELRY),
      cart_type: CartTypeEnum.ORDER_CART,
      item_ids: itemIds,
      price_mode: priceMode,
      item_cart_attributes: itemCartAttributes,
      ...(buyNow && { buy_now: true }),
    };

    let startedCheckout = false;
    skipCloseRef.current = true;
    setPendingConfirmAction(mode);
    startLoading();
    bulkActionsCall.mutate(payload, {
      onSuccess(data) {
        if (data?.data?.response?.header?.status === 200) {
          if (!buyNow) {
            queryClient
              .invalidateQueries({ queryKey: ["GET_CART_ITEM"] })
              .then();
            const totalCartItems = data?.data?.response?.body?.total_cart_items;
            if (typeof totalCartItems === "number") {
              dispatch(updateTotalCartItems(totalCartItems));
            }
          }
          const body = data?.data?.response?.body;
          const message = data?.data?.response?.header?.message;
          const notAddedData = body?.not_added_data;
          const failedCount =
            notAddedData?.not_added_due_to_is_customizable ?? 0;
          const allFailed = failedCount > 0 && failedCount === itemIds?.length;
          const someFailed = failedCount > 0;

          if (allFailed) {
            showToast(
              "",
              `(${failedCount})` +
                t(
                  "global.addToCart.message.text",
                  "item(s) could not be added due to technical limitations "
                ),
              ToastTypes.ERROR,
              true,
              true,
              80
            );
            return;
          }

          if (someFailed) {
            showToast(
              "",
              `(${failedCount})` +
                t(
                  "global.addToCart.message.text",
                  "item(s) could not be added due to technical limitations "
                ),
              ToastTypes.SUCCESS,
              true,
              true,
              80
            );
          } else if (!buyNow) {
            showToast("", message, ToastTypes.SUCCESS, true, true, 80);
          }

          if (buyNow) {
            const sessionId = body?.buy_now_session_id;
            if (!sessionId) {
              showToast(
                t("orderCart.offline.error.title", "Order Failed"),
                t(
                  mode === "quote"
                    ? "orderNowVariation.sendQuote.error.message"
                    : "orderCart.offline.error.message",
                  mode === "quote"
                    ? "Failed to send your quote. Please try again."
                    : "Failed to create your order. Please try again."
                ),
                ToastTypes.ERROR,
                true,
                true
              );
              return;
            }
            startedCheckout = true;
            if (mode === "quote") {
              placeQuoteCheckout(sessionId);
            } else {
              placeOrderCheckout(sessionId);
            }
          }
        }
      },
      onSettled() {
        if (!startedCheckout) {
          setPendingConfirmAction(null);
          stopLoading();
        }
      },
    });
  };

  const renderConfigCard = (config: ConfigEntry) => {
    const displayItem = getCellItem(config.id);
    return (
      <ItemSection key={String(config.id)}>
        <ImageContainer theme={theme}>
          <ImageComponentJewelry
            mItem={displayItem}
            addNewConfig={
              isConfigurableJewelryItem(config.baseItem)
                ? () => addNewConfiguration(config)
                : undefined
            }
            isChecked={selectedCellIds.includes(config.id)}
            onCheckChange={(checked) => toggleCellSelection(config.id, checked)}
            onDelete={() => requestRemoveConfigurations([config.id])}
          />
        </ImageContainer>
        <JewelryConfigurationCell
          key={String(config.id)}
          mItem={displayItem}
          variationData={config.variationData}
          isLast
          isFirst
          selections={cellSelections[config.id]}
          showPersonalization={
            isPersonalizationSectionEnabled &&
            !!displayItem?.custom_design_configuration
          }
          personalizationTitle={t(
            (personalizationSectionLayout as any)?.label ?? ""
          )}
          personalizationSubOptions={
            personalizationSectionLayout?.["sub-sections"]
          }
          personalization={cellPersonalization[config.id]}
          showComments={isSendQuote}
          comments={cellComments[config.id]}
          showValidation={showConfigErrors}
          onSelectionChange={(optionName, value) =>
            handleSelectionChange(config.id, optionName, value)
          }
          onPersonalizationChange={(values) =>
            setCellPersonalization((prev) => ({
              ...prev,
              [config.id]: values,
            }))
          }
          onCommentsChange={(value) =>
            setCellComments((prev) => ({
              ...prev,
              [config.id]: value,
            }))
          }
          onInfoChange={(info) =>
            setCellInfo((prev) => {
              const existing = prev[config.id];
              const fallbackVariantId = getVariationMeta(
                config.variationData
              ).variantId;
              return {
                ...prev,
                [config.id]: {
                  quantity: info.quantity ?? existing?.quantity ?? 1,
                  totalPrice: info.totalPrice ?? existing?.totalPrice ?? null,
                  variantId:
                    info.variantId ?? existing?.variantId ?? fallbackVariantId,
                },
              };
            })
          }
        />
      </ItemSection>
    );
  };

  const selectionHeader =
    configs.length > 0 ? (
      <OrderNowSelectionHeader
        totalItems={configs.length}
        selectedCount={selectedCellIds.length}
        allSelected={allCellsSelected}
        onToggleAll={toggleSelectAll}
        onDeleteSelected={() => requestRemoveConfigurations(selectedCellIds)}
      />
    ) : null;

  const useQuoteLeaveCopy =
    isSendQuote && userInfo.enableOrderCartQuote() === true;

  const itemListView = (
    <ChildContainer1>
      {!theme.isMobile && selectionHeader}
      <ListItemsContainer>
        {configs.map((config) => renderConfigCard(config))}
      </ListItemsContainer>
    </ChildContainer1>
  );

  return (
    <OverLay>
      <LayoutContainer>
        <WebPageHeader
          LeftItems={HeaderTitleContainer}
          hideBack={theme.isTablet}
          showBackDivider
          testID={"order-now-jewelry-variation.go-back-button"}
          dataTestId={"order-now-jewelry-variation.go-back-button"}
        />
        {configs.length === 0 ? (
          <EmptyView
            deepLink={orderCart?.bottom_buttons?.continue_shopping?.link}
          />
        ) : (
          <>
            {isApiLoading && (
              <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
            )}
            <KeyboardAwareScrollView
              style={{
                flex: 1,
                backgroundColor: theme["core-02"],
              }}
              contentContainerStyle={{ flexGrow: 1, width: "100%" }}
              keyboardShouldPersistTaps="always"
            >
              {theme.isMobile && selectionHeader}
              <SplitView>
                {itemListView}
                <OrderNowRequestForm
                  formData={formData}
                  orderTypeFormData={orderTypeFormData}
                  totalNetPrice={totalNetPrice}
                  productType={productType}
                  priceMode={priceMode}
                  showFormFirst={isSendQuote}
                  couponCode={couponCode}
                  setCouponCode={setCouponCode}
                  refetchCartData={refetchPromoCart}
                  cartData={promoCartData}
                  cartAPIFetching={promoFetching}
                />
              </SplitView>
            </KeyboardAwareScrollView>
            <OrderNowBottomBar
              totalNetPrice={totalNetPrice}
              shippingFee={shippingFee}
              promoDiscount={promoDiscount}
              formattedPrice={formattedPrice}
              isConfirmEvaluating={isConfirmEvaluating}
              isAllConfigsComplete={isAllConfigsComplete}
              selectedCount={selectedCellIds.length}
              selectedQuantity={selectedQuantity}
              pendingAction={pendingConfirmAction}
              formData={formData}
              priceMode={priceMode}
              isSendQuote={isSendQuote}
              onAddToCart={() => confirmOrder("cart")}
              onPlaceOrder={requestPlaceOrder}
              onSendQuote={requestSendQuote}
              onIncompleteConfirm={() => setShowConfigErrors(true)}
            />
          </>
        )}
      </LayoutContainer>
      <Modal
        headerTitle={t("orderCart.successPopUp.title.text")}
        isOpen={showSuccessModal}
        onClose={() => {
          queryClient.invalidateQueries({ queryKey: ["GET_CART_ITEM"] }).then();
          setShowSuccessModal(false);
        }}
        size={408}
        noPadding
      >
        <SuccessPopUp
          successData={orderCheckout.data?.data?.response?.body?.result?.order}
          successPopUpConfig={orderCart?.successPopUpConfig}
          deepLink={orderCart?.successButton?.deeplink}
          productType={productType}
          priceMode={priceMode}
          onClose={() => {
            queryClient
              .invalidateQueries({ queryKey: ["GET_CART_ITEM"] })
              .then();
            setShowSuccessModal(false);
          }}
        />
      </Modal>
      <ConfirmationPopup
        title={t("bmh.canRequestPopUp.title.text")}
        htmlMessage={t("bmh.canRequestPopUp.description.text", {
          x: settings?.organization?.emails?.support
            ? settings.organization.emails.support
            : "support@vdbapp.com",
        })}
        primaryButtonTitle={t("bmh.canRequestPopUp.button.title.text")}
        primaryButtonAction={() => setCanRequestModal(false)}
        category={ButtonCategory.jewelry}
        messageType={messageType.Generic}
        opened={canRequestModal}
      />
      <ConfirmationPopup
        title={t(
          "bulkInitScreen.confirmPlaceRequest.confirmPopup.title.text",
          "Confirm Order"
        )}
        htmlMessage={t(
          "orderNowVariation.confirmPlaceRequest.confirmPopup.description.text",
          "Are you sure you want to place the order?"
        )}
        primaryButtonTitle={t(
          "bulkInitScreen.confirmPlaceRequest.confirmPopup.button.primary.text",
          "Confirm"
        )}
        secondaryButtonTitle={t(
          "bulkInitScreen.confirmPlaceRequest.confirmPopup.button.secondary.text",
          "Cancel"
        )}
        primaryButtonAction={() => {
          setShowPlaceOrderConfirm(false);
          confirmOrder("order");
        }}
        secondaryButtonAction={() => setShowPlaceOrderConfirm(false)}
        primaryButtonLoading={pendingConfirmAction === "order"}
        primaryButtonDisabled={pendingConfirmAction === "order"}
        category={ButtonCategory.default}
        messageType={messageType.Warning}
        opened={showPlaceOrderConfirm}
      />
      <ConfirmationPopup
        title={t(
          "orderNowVariation.sendQuote.confirmPopup.title.text",
          "Confirmation"
        )}
        htmlMessage={t(
          "orderNowVariation.sendQuote.confirmPopup.description.text",
          {
            count: selectedConfigs.length,
            defaultValue: `Are you sure you want to share a quote for ${selectedConfigs.length} styles?`,
          }
        )}
        primaryButtonTitle={t(
          "orderNowVariation.sendQuote.confirmPopup.button.proceed.text",
          "Proceed"
        )}
        secondaryButtonTitle={t(
          "orderNowVariation.sendQuote.confirmPopup.button.cancel.text",
          "Cancel"
        )}
        primaryButtonAction={() => {
          setShowSendQuoteConfirm(false);
          confirmOrder("quote");
        }}
        secondaryButtonAction={() => setShowSendQuoteConfirm(false)}
        primaryButtonLoading={pendingConfirmAction === "quote"}
        primaryButtonDisabled={pendingConfirmAction === "quote"}
        category={ButtonCategory.default}
        messageType={messageType.Generic}
        opened={showSendQuoteConfirm}
      />
      <ConfirmationPopup
        title={t(
          "bulkInitScreen.placeRequestSuccess.confirmPopup.quote.title.text"
        )}
        htmlMessage={t(
          "bulkInitScreen.placeRequestSuccess.confirmPopup.quote.description.text"
        )}
        primaryButtonTitle={t(
          "bulkInitScreen.placeRequestSuccess.confirmPopup.quote.primary.text"
        )}
        secondaryButtonTitle={t(
          "bulkInitScreen.placeRequestSuccess.confirmPopup.quote.secondary.text"
        )}
        primaryButtonAction={() => {
          requestClearRecipientInfo();
          setShowQuoteSuccessPopup(false);
        }}
        secondaryButtonAction={() => {
          const request = quoteSuccessRequestRef.current ?? quoteSuccessRequest;
          const requestId = request?.id;
          if (requestId == null) return;
          setShowQuoteSuccessPopup(false);
          allowLeaveRef.current = true;
          OrderNowLeaveGuard.setActive(false);
          OrderNowLeaveGuard.allowNextRemoval();
          const navigateParams = {
            selectedTab: "sent",
            initial: false,
            screen: Routes.RequestDetailScreen,
            requestedItem: request,
            params: {
              id: requestId,
              selectedTab: "sent",
              requestName: RequestTypes.Order,
              productType: productType ?? ProductCategory.JEWELRY,
              externalPriceMode: priceMode,
              price_mode: priceMode,
            },
          };
          if (theme.isMobile) {
            navigate(Routes.GlobalNavigator, navigateParams);
          } else {
            navigate(Routes.AppNavigator, {
              screen: Routes.RequestHistoryNavigator,
              params: navigateParams,
            });
          }
        }}
        primaryButtonLoading={false}
        category={ButtonCategory.jewelry}
        messageType={messageType.Success}
        opened={showQuoteSuccessPopup}
      />
      {removeConfirmPopup.openPopup && removeConfirmPopup.ids.length > 0 ? (
        <PopUp
          title={t(
            "orderNowVariation.removeFromList.confirmPopup.title.text",
            "Remove item(s)?"
          )}
          buttons={[
            {
              type: ButtonType.primary,
              title: t(
                "bulkInitScreen.removeFromList.confirmPopup.button.primary.text"
              ),
              onPressAction: confirmRemoveConfigurations,
            },
            {
              type: ButtonType.secondary,
              title: t(
                "bulkInitScreen.removeFromList.confirmPopup.button.secondary.text"
              ),
              onPressAction: closeRemoveConfirmPopup,
            },
          ]}
          HtmlSource={{
            html: t(
              "orderNowVariation.removeFromList.confirmPopup.description.text",
              "Are you sure you want to remove the item(s)?"
            ),
          }}
          styleKey="Body-R"
          type={messageType.Error}
        />
      ) : null}
      <ConfirmationPopup
        title={t(
          useQuoteLeaveCopy
            ? "orderNowVariation.sendQuote.leaveConfirm.title.text"
            : "orderNowVariation.leaveConfirm.title.text",
          "Are you sure you want to leave?"
        )}
        htmlMessage={t(
          useQuoteLeaveCopy
            ? "orderNowVariation.sendQuote.leaveConfirm.description.text"
            : "orderNowVariation.leaveConfirm.description.text",
          useQuoteLeaveCopy
            ? "If you leave this page, your quote will be lost."
            : "If you leave this page, your order will be lost."
        )}
        primaryButtonTitle={t(
          useQuoteLeaveCopy
            ? "orderNowVariation.sendQuote.leaveConfirm.button.leave.text"
            : "orderNowVariation.leaveConfirm.button.leave.text",
          "Leave this page"
        )}
        secondaryButtonTitle={t(
          useQuoteLeaveCopy
            ? "orderNowVariation.sendQuote.leaveConfirm.button.stay.text"
            : "orderNowVariation.leaveConfirm.button.stay.text",
          useQuoteLeaveCopy ? "Continue with Quote" : "Continue with Order"
        )}
        primaryButtonAction={confirmLeave}
        secondaryButtonAction={cancelLeave}
        category={ButtonCategory.default}
        messageType={messageType.Warning}
        opened={showLeaveConfirm}
      />
    </OverLay>
  );
};

const OrderNowJewelryVariationScreen = (
  props: OrderNowJewelryVariationScreenProps
) => (
  <DynamicFormProvider>
    <OrderNowJewelryVariationContent {...props} />
  </DynamicFormProvider>
);

const OverLay = styled.View`
  flex: 1;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

const SplitView = styled.View`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "column" : "row"};
  align-self: stretch;
  justify-content: space-between;
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile
      ? `padding-horizontal: ${SpaceType.Basic}px;`
      : `margin-horizontal: ${
          theme.isLargeScreen ? "160px" : `${SpaceType.Big}px`
        };`}
`;

const ChildContainer1 = styled.View`
  width: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "100%" : theme.isLargeScreen ? "55%" : "60%"};
  order: 1;
`;

const ChildContainer2 = styled.View`
  width: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "100%" : theme.isLargeScreen ? "42%" : "38%"};
  order: 2;
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile && `margin-top: ${SpaceType.Big}px;`}
`;

const ListItemsContainer = styled.View`
  width: 100%;
  gap: 8px;
`;

const RequestFormContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  width: 100%;
  padding: ${SpaceType.Double}px;
`;

const RowContainer = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  justify-content: flex-start;
  align-items: center;
`;

const ButtonView = styled.View<{ insets: { bottom: number } }>`
  width: 100%;
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile ? `${SpaceType.Big}px` : `${SpaceType.Double}px`};
  padding-bottom: ${({
    theme,
    insets,
  }: {
    theme: DefaultTheme;
    insets: { bottom: number };
  }) =>
    !theme.isMobile
      ? `${SpaceType.Big}px`
      : `${
          (Platform.OS === "web" ? SpaceType.Double : SpaceType.ExtraLarge) +
          (Platform.OS === "android" && insets.bottom > 0 ? insets.bottom : 0)
        }px`};
  padding-horizontal: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? "160px" : `${SpaceType.Double}px`};
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  align-items: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "stretch" : "flex-end"};
`;

const ButtonContainer = styled.View`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "column" : "row"};
  justify-content: space-between;
  align-items: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "stretch" : "center"};
  width: 100%;
  gap: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? SpaceType.Double : 30}px;
`;

const ButtonFlexContainer2 = styled.View`
  display: flex;
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "column" : "row"};
  gap: ${SpaceType.Basic}px;
  width: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile ? "42%" : "100%"};
`;

const ButtonSlot = styled.View`
  position: relative;
  flex: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "none" : "1"};
  width: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "100%" : "auto"};
`;

const MissingConfigTooltip = styled.View`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 3px;
  align-items: center;
  z-index: 20;
`;

const MissingConfigTooltipBubble = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-06"]};
  padding: 8px;
  border-radius: 8px;
  width: 100%;
`;

const MissingConfigTooltipArrow = styled.View`
  width: 0px;
  height: 0px;
  border-left-width: 8px;
  border-right-width: 8px;
  border-top-width: 8px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-06"]};
`;

const TotalPriceRow = styled.View`
  flex-direction: row;
  justify-content: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "space-between" : "flex-start"};
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const TotalLabelRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const ToolTipRow = styled.View`
  flex-direction: row;
  align-items: center;
`;

const ToolTipView = styled.View`
  gap: ${SpaceType.ExtraSmall}px;
`;

const DisclaimerContainer = styled.View`
  gap: ${SpaceType.ExtraSmall}px;
  width: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile ? "52%" : "100%"};
`;

const SelectionHeader = styled.View`
  width: 100%;
  align-self: stretch;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? 0 : 8}px;
  border-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile &&
    `
      border-left-width: 0px;
      border-right-width: 0px;
      border-top-width: 0px;
    `}
  padding-vertical: 8px;
  padding-horizontal: 16px;
  margin-top: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? 0 : 8}px;
  margin-bottom: 8px;
`;

const SelectionLeft = styled.View`
  flex-direction: row;
  align-items: center;
  flex: 1;
  min-width: 0;
  margin-right: 12px;
`;

const SelectionDeleteButton = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ItemSection = styled.View`
  width: 100%;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  border-width: 1px;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  overflow: hidden;
`;

const ImageContainer = styled.View<any>``;

export default OrderNowJewelryVariationScreen;
